// src/pages/Dashboard.js
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { subscribeTransaction } from "../services/saleng";
import {
  Container,
  Grid2,
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  SvgIcon,
} from "@mui/material";

import { ReactComponent as CoSvg } from "../images/CO2.svg";
import { ReactComponent as TreeSvg } from "../images/tree.svg";

import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from "chart.js";

ChartJS.register(Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const Dashboard = () => {
  const { transactionId } = useParams();
  const [score, setScore] = useState(null);
  const [trees, setTrees] = useState(null);
  const [history, setHistory] = useState([]);
  const [groups, setGroups] = useState({});

  useEffect(() => {
    const es = subscribeTransaction(
      (data) => {
        setScore(data.totalScore);
        setTrees(data.treesSaved);
        setGroups(data.groups ?? {});
        setHistory((prev) => [
          ...prev.slice(-19),
          { time: new Date().toLocaleTimeString(), score: data.totalScore },
        ]);
      },
      (err) => console.error("SSE error:", err)
    );
    return () => es?.close();
  }, []);

  const barData = {
    labels: Object.keys(groups),
    datasets: [
      {
        label: "น้ำหนักรวม (kg)",
        data: Object.values(groups).map((g) => g.weight ?? 0),
        backgroundColor: [
          "#4caf50", // green
          "#2196f3", // blue
          "#ff9800", // orange
          "#f44336", // red
          "#9c27b0", // purple
          "#00bcd4", // cyan
        ],
      },
    ],
  };

  return (
    <div style={{ backgroundColor: "#E3F2FD", minHeight: "100vh" }}>
      <Container sx={{ pt:2, overflowY: "auto", color: "#042CA6" }}>
        <Typography
          variant="h3"
          gutterBottom
          align="center" // ✅ จัดกลาง
          sx={{ fontWeight: "bold", fontFamily: "IBM Plex Sans Thai"  }} // (เสริมให้เด่นขึ้นได้)
        >
          คุณได้ช่วยโลกไว้แล้ว!
        </Typography>

        <Grid2 container spacing={3}>
          <Grid2 size={6}>
            <Card sx={{ p: 2, textAlign: "center", fontFamily: "IBM Plex Sans Thai" }}>
              <CardContent>
                <Stack spacing={1} alignItems="center">
                  {/* Icon อยู่บน */}
                  <Box
                    sx={{
                      width: 150,
                      height: 150,
                      color: "primary.main",
                    }}
                  >
                    <SvgIcon
                      component={CoSvg}
                      inheritViewBox
                      sx={{ fontSize: 120, color: "green" }} // ใช้ theme หรือกำหนดตรง ๆ ได้
                    />
                  </Box>

                  {/* ข้อความกลาง */}
                  <Typography variant="h6" color="green" sx={{ fontFamily: "IBM Plex Sans Thai", fontSize: 26 }} gutterBottom>
                    ลดการปล่อยก๊าซ CO₂ สะสม
                  </Typography>

                  {/* ตัวเลข + หน่วย */}
                  <Typography
                    variant="h3"
                    color="green"
                    sx={{ fontWeight: "bold" }}
                  >
                    {score !== null ? score.toLocaleString("th-TH") : "-"}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontFamily: "IBM Plex Sans Thai", fontSize: 18 }} color="green">
                    kgCO₂e
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={6}>
            <Card sx={{ p: 2, textAlign: "center" }}>
              <CardContent>
                <Stack spacing={1} alignItems="center">
                  {/* Icon อยู่บน */}
                  <Box
                    sx={{
                      width: 150,
                      height: 150,
                      color: "#042CA6",
                    }}
                  >
                    <SvgIcon
                      component={TreeSvg}
                      inheritViewBox
                      sx={{ fontSize: 120, color: "green" }} // ใช้ theme หรือกำหนดตรง ๆ ได้
                    />
                  </Box>

                  {/* ข้อความกลาง */}
                  <Typography variant="h6" color="green" sx={{ fontFamily: "IBM Plex Sans Thai", fontSize: 26 }} gutterBottom>
                    เทียบเท่ากับปลูกต้นไม้
                  </Typography>

                  {/* ตัวเลข + หน่วย */}
                  <Typography
                    variant="h3"
                    color="green"
                    sx={{ fontWeight: "bold" }}
                  >
                    {trees !== null ? trees.toLocaleString("th-TH") : "-"}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontFamily: "IBM Plex Sans Thai", fontSize: 18 }} color="green">
                    ต้น
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Grid2>

          {/* chart */}
          <Grid2 size={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="green" gutterBottom sx={{ fontFamily: "IBM Plex Sans Thai", fontSize: 20 }} align="center">
                  กราฟแสดงขยะแต่ละประเภท
                </Typography>
                <Box
                  sx={{
                    height: 300,
                    display: "flex",
                    justifyContent: "center", // จัดกลางแนวนอน
                  }}
                >
                  <Box sx={{ width: "70%", height: 300 }}>
                    {" "}
                    {/* กำหนดขนาด chart */}
                    <Bar
                      data={barData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { position: "top" } },
                        scales: {
                          x: { ticks: { font: { size: 14 } } },
                          y: {
                            beginAtZero: true,
                            ticks: { font: { size: 14 } },
                          },
                        },
                        datasets: {
                          bar: { barPercentage: 0.6, categoryPercentage: 0.8 },
                        },
                      }}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>
      </Container>
    </div>
  );
};

export default Dashboard;
